import { AKComponent } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';
import { debounce } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends AKComponent<Refs>
 */
export class MediaGallery extends AKComponent {
  /**
   * The media list container (ul element).
   *
   * @type {HTMLElement | null}
   */
  #mediaList = null;

  /**
   * The thumbnail list container (ul element).
   *
   * @type {HTMLElement | null}
   */
  #thumbnailList = null;

  #fromClick = false;

  /**
   * The intersection observer for tracking visible media items.
   *
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  #debouncedSyncArrows = debounce(() => {
    this.#syncCarouselArrowButtons();
  }, 80);

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    if (this.dataset.ignoreVariantGallery !== 'true') {
      target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    }
    const mediaListOrScroller =
      this.refs['mediaList'] ?? this.refs['scroller'];
    this.#mediaList =
      mediaListOrScroller instanceof HTMLElement ? mediaListOrScroller : null;
    this.#thumbnailList = this.refs['thumbnailsContainer'] instanceof HTMLElement ? this.refs['thumbnailsContainer'] : null;
    // Initialise l'observateur d'intersection pour détecter les changements de position
    this.#setupIntersectionObserver();
    this.#initStandaloneIndicators();

    if (!this.querySelector('slideshow-component') && this.#mediaList) {
      this.#mediaList.addEventListener('scroll', this.#debouncedSyncArrows, { signal });
      if ('onscrollend' in this.#mediaList) {
        this.#mediaList.addEventListener('scrollend', this.#debouncedSyncArrows, { signal });
      }
      this.#syncCarouselArrowButtons();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#cleanupIntersectionObserver();
  }

  /**
   * Sets up the intersection observer to track visible media items.
   */
  #setupIntersectionObserver() {
    if (!this.#mediaList) return;

    // Nettoyer l'observateur existant s'il y en a un
    this.#cleanupIntersectionObserver();

    // Récupérer tous les éléments li
    const mediaItems = Array.from(this.#mediaList.querySelectorAll('li'));
    if (!mediaItems.length) return;

    // Configuration de l'IntersectionObserver
    const options = {
      root: this.#mediaList, // Le conteneur de scroll
      rootMargin: '0px',
      threshold: 0.5 // L'élément doit être au moins 50% visible
    };

    // Fonction debounced pour éviter trop d'appels
    const debouncedCallback = debounce((visibleElement) => {
      const currentIndex = mediaItems.indexOf(visibleElement);
      if (currentIndex !== -1) {
        this.#setActiveThumbnail(currentIndex);
        this.#updateStandalonePaginationDots(currentIndex);
        this.#syncCarouselArrowButtons(currentIndex);
      }
    }, 100);

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      // Trouver l'élément qui devient visible (il n'y en a qu'un à la fois avec CSS snap)
      const visibleEntry = entries.find(entry => entry.isIntersecting);
      
      if (visibleEntry) {
        debouncedCallback(visibleEntry.target);
      }
    }, options);

    // Observer tous les éléments li
    mediaItems.forEach(item => {
      this.#intersectionObserver?.observe(item);
    });
  }

  /**
   * Cleans up the intersection observer.
   */
  #cleanupIntersectionObserver() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Désactive prev/next en bord de liste (galerie sans slideshow-component).
   *
   * @param {number} [forcedIndex] - Index courant si déjà connu.
   */
  #syncCarouselArrowButtons(forcedIndex) {
    if (this.querySelector('slideshow-component')) return;

    const prev = this.refs.previous;
    const next = this.refs.next;
    if (!(prev instanceof HTMLButtonElement) && !(next instanceof HTMLButtonElement)) return;

    const slides = this.#getMediaSlideElements();
    if (slides.length === 0) return;

    const idx =
      typeof forcedIndex === 'number' && !Number.isNaN(forcedIndex)
        ? Math.min(Math.max(0, forcedIndex), slides.length - 1)
        : this.#getCurrentSlideIndex(slides);

    const atStart = idx <= 0;
    const atEnd = idx >= slides.length - 1;

    if (prev instanceof HTMLButtonElement) {
      prev.disabled = atStart;
      if (atStart) {
        prev.setAttribute('aria-disabled', 'true');
        prev.setAttribute('data-carousel-nav', 'inactive');
      } else {
        prev.removeAttribute('aria-disabled');
        prev.removeAttribute('data-carousel-nav');
      }
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = atEnd;
      if (atEnd) {
        next.setAttribute('aria-disabled', 'true');
        next.setAttribute('data-carousel-nav', 'inactive');
      } else {
        next.removeAttribute('aria-disabled');
        next.removeAttribute('data-carousel-nav');
      }
    }
  }

  /**
   * Conteneur des points (pagination) lorsqu'il n'y a pas de slideshow-component
   * (ex. desktop en vignettes + points sur mobile).
   *
   * @returns {HTMLElement | null}
   */
  #getStandaloneIndicatorsContainer() {
    if (this.querySelector('slideshow-component')) return null;
    const raw = this.refs.indicators;
    const el = Array.isArray(raw) ? raw[0] : raw;
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Crée les points dans ref="indicators" quand le slideshow ne gère pas la pagination.
   */
  #initStandaloneIndicators() {
    const indicators = this.#getStandaloneIndicatorsContainer();
    if (!indicators) return;

    const slides = this.#getMediaSlideElements();
    indicators.replaceChildren();

    if (slides.length <= 1) return;

    for (let i = 0; i < slides.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `c-dot${i === 0 ? ' c-dot--active' : ''}`;
      dot.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      dot.setAttribute('aria-label', `Image ${i + 1} sur ${slides.length}`);
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleThumbnailClick(i);
      });
      indicators.appendChild(dot);
    }
  }

  /**
   * @param {number} index
   */
  #updateStandalonePaginationDots(index) {
    const indicators = this.#getStandaloneIndicatorsContainer();
    if (!indicators) return;

    const dots = indicators.querySelectorAll('.c-dot');
    if (!dots.length) return;

    dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('c-dot--active', active);
      dot.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#stepGallerySlide(-1);
  }

  /**
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#stepGallerySlide(1);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getMediaSlideElements() {
    if (!this.#mediaList) return [];
    return Array.from(this.#mediaList.querySelectorAll(':scope > li'));
  }

  /**
   * @param {HTMLElement[]} slides
   * @returns {number}
   */
  #getCurrentSlideIndex(slides) {
    if (!this.#mediaList || slides.length === 0) return 0;

    const { scrollLeft, clientWidth } = this.#mediaList;
    const center = scrollLeft + clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (!slide) continue;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  /**
   * @param {number} delta -1 = previous, 1 = next
   */
  #stepGallerySlide(delta) {
    const slides = this.#getMediaSlideElements();
    if (slides.length < 2) return;

    const current = this.#getCurrentSlideIndex(slides);
    const nextIndex = Math.min(Math.max(0, current + delta), slides.length - 1);
    if (nextIndex === current) return;

    this.#goToSlideIndex(nextIndex);
  }

  /**
   * @param {number} index
   */
  #goToSlideIndex(index) {
    const slides = this.#getMediaSlideElements();
    if (index < 0 || index >= slides.length) return;

    const targetItem = slides[index];
    if (!targetItem) return;

    this.#fromClick = true;

    if (this.#thumbnailList) {
      const thumbnails = this.#thumbnailList.querySelectorAll('button');
      for (const thumbnail of thumbnails) {
        thumbnail.classList.remove('c-media__thumb--active');
      }
      const thumbnail = this.#thumbnailList.querySelector(`button[data-index="${index}"]`);
      if (thumbnail instanceof HTMLElement) {
        thumbnail.classList.add('c-media__thumb--active');
        this.#scrollThumbnailIntoView(thumbnail);
      }
    }

    targetItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });

    this.#updateStandalonePaginationDots(index);
    this.#syncCarouselArrowButtons(index);
    this.#handleScrollEnd();
  }

  /**
   * @param {number} index
   */
  handleThumbnailClick(index) {
    this.#fromClick = true;
    if (!this.#mediaList) {
      return;
    }

    // Récupère tous les éléments li enfants
    const mediaItems = Array.from(this.#mediaList.querySelectorAll('li'));
    
    if (index < 0 || index >= mediaItems.length) {
      return;
    }

    // Trouve l'élément li correspondant à l'index
    const targetItem = mediaItems[index];
    
    if (targetItem) {
      // ajout de la class active au thumbnail correspondant
      // et suppression de la class active de tous les autres thumbnails
      if (this.#thumbnailList) {
        const thumbnails = this.#thumbnailList.querySelectorAll('button');
        thumbnails.forEach(thumbnail => {
          thumbnail.classList.remove('c-media__thumb--active');
        });
        const thumbnail = this.#thumbnailList.querySelector(`button[data-index="${index}"]`);
        if (thumbnail instanceof HTMLElement) {
          thumbnail.classList.add('c-media__thumb--active');
          this.#scrollThumbnailIntoView(thumbnail);
        }
      }

      // Fait défiler vers l'élément cible en utilisant scrollIntoView
      // avec smooth behavior pour une transition fluide
      targetItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      
      this.#updateStandalonePaginationDots(index);
      this.#syncCarouselArrowButtons(index);
      // Remettre fromClick à false une fois le scroll terminé
      this.#handleScrollEnd();
    }
  }

  /**
   * Scrolls the thumbnail into view so it is the leftmost visible element in the thumbnails slider.
   * @param {HTMLElement} thumbnail - The thumbnail element to scroll into view.
   */
  #scrollThumbnailIntoView(thumbnail) {
    if (!thumbnail || !this.#thumbnailList) return;
    const scrollableThumbnails =
      this.#thumbnailList.querySelector('.c-media__thumbnails') ?? this.#thumbnailList.firstElementChild;
    if (!(scrollableThumbnails instanceof HTMLElement)) return;
    scrollIntoView(thumbnail, {
      ancestor: scrollableThumbnails,
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });
  }

  /**
   * Sets the active thumbnail based on the current media index.
   * @param {number} index - The index of the active media item.
   */
  #setActiveThumbnail(index) {
    if (!this.#thumbnailList || this.#fromClick) return;
    const thumbnails = this.#thumbnailList.querySelectorAll('button');
    thumbnails.forEach(thumbnail => {
      thumbnail.classList.remove('c-media__thumb--active');
    });
    const thumbnail = this.#thumbnailList.querySelector(`button[data-index="${index}"]`);
    if (thumbnail instanceof HTMLElement) {
      thumbnail.classList.add('c-media__thumb--active');
      this.#scrollThumbnailIntoView(thumbnail);
    }
  }

  /**
   * Handles the end of scroll animation to reset fromClick flag.
   */
  #handleScrollEnd() {
    if (!this.#mediaList) return;

    // Utiliser l'événement scrollend si supporté, sinon fallback avec setTimeout
    if ('onscrollend' in this.#mediaList) {
      const handleScrollEnd = () => {
        this.#fromClick = false;
        this.#syncCarouselArrowButtons();
        this.#mediaList?.removeEventListener('scrollend', handleScrollEnd);
      };
      this.#mediaList.addEventListener('scrollend', handleScrollEnd, { once: true });
    } else {
      // Fallback pour les navigateurs qui ne supportent pas scrollend
      // Estimer la durée du scroll smooth (généralement 300-500ms)
      setTimeout(() => {
        this.#fromClick = false;
        this.#syncCarouselArrowButtons();
      }, 500);
    }
  }

}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
